import Vue from 'vue';
import Vuex from 'vuex';
import cloneDeep from 'lodash/cloneDeep';

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    cart: {},
    cartItems: [],
    cartItemsConcept: [],
    modalIsOpen: false,
    alertIsOpen: false,
    openDatePicker: null,
    isLoading: false,
    errors: [],
  },

  getters: {
    cart: (state) => state.cart,
    cartItems: (state) => state.cartItems,
    cartItemsConcept: (state) => state.cartItemsConcept,
    modalIsOpen: (state) => state.modalIsOpen,
    alertIsOpen: (state) => state.alertIsOpen,
    isDatePickerOpen: (state) => (id) => state.openDatePicker === id,
    isLoading: (state) => state.isLoading,
    errors: (state) => state.errors,
    cartIsValid: (state) => {
      const validCartItems = state.cartItems.filter((cartItem) => {
        if (typeof cartItem.roomOptions === 'undefined') {
          return false;
        }

        return cartItem.roomOptions.filter((roomOption) => {
          return !roomOption.selectedOption;
        }).length === 0;
      });

      return validCartItems.length === state.cartItems.length;
    },
  },

  actions: {
    getCart: async ({ commit }) => {
      commit('setIsLoading', true);

      try {
        const response = await window.axios.get(window.app.api.journeyCart.show);
        const { cart, cartItems } = await response.data;

        await commit('setCart', cart);
        await commit('setCartItems', cartItems);
        await commit('setCartItemsConcept', cartItems);
        await commit('setIsLoading', false);
      } catch (error) {
        commit('setIsLoading', false);
        // console.error(error);
      }
    },

    storeCart: async ({ commit, dispatch, state }, redirect) => {
      commit('setIsLoading', true);

      try {
        await window.axios.post(window.app.api.journeyCart.store, {
          cartItems: state.cartItems,
        });

        await dispatch('browseToPage', redirect);
        await commit('setIsLoading', false);
      } catch (error) {
        commit('setIsLoading', false);
        // console.error(error);
      }
    },

    removeCartItem: async ({ commit, dispatch }, id) => {
      commit('setIsLoading', true);

      try {
        await window.axios.delete(window.app.api.journeyCartItem.destroy, {
          data: {
            journey_cart_item_id: id,
          },
        });

        await dispatch('getCart');
        await commit('setIsLoading', false);
      } catch (error) {
        commit('setIsLoading', false);
        // console.error(error)
      }
    },

    recalculateCartItems: async ({ commit, state }) => {
      commit('setIsLoading', true);

      try {
        await window.axios.post(window.app.api.travelOrder.validate, {
          dateOfArrival: state.cart.dateOfArrival,
          cartItems: state.cartItemsConcept,
        });

        await window.location.reload();
        await commit('setIsLoading', false);
      } catch (error) {
        commit('setIsLoading', false);
        // console.error(error);
      }
    },

    onCartItemRoomOptionChange({ commit }, [roomIndex, cartItem, roomOptionId]) {
      const roomOptions = [...cartItem.roomOptions];

      roomOptions[roomIndex].selectedOption = roomOptionId;

      commit('setRoomOptionsForCartItem', [cartItem.id, roomOptions]);
    },

    onCartItemOptionChange({ commit }, [checked, cartItem, optionId]) {
      let options = [...cartItem.options];

      options = options.map((option) => {
        if (option.id === optionId) {
          option.selected = checked;
        }

        return option;
      });

      commit('setOptionsForCartItem', [cartItem.id, options]);
    },

    onCartItemExperienceChange({ commit }, [checked, cartItem, experienceId, quantity = 1]) {
      let experiences = [...cartItem.experiences];

      experiences = experiences.map((option) => {
        if (option.id === experienceId) {
          option.selected = checked;
          option.quantity = parseInt(quantity, 10);
        }

        return option;
      });

      commit('setExperiencesForCartItem', [cartItem.id, experiences]);
    },

    getRoomOptions: async ({ commit }, [id, room]) => {
      try {
        const response = await window.axios.post(window.app.api.roomOption, {
          id,
          room,
        });
        const { options } = await response.data;

        await commit('setRoomOptionsForCartItem', [id, options]);
      } catch (error) {
        // console.error(error)
      }
    },

    browseToPage(context, to) {
      window.location.href = to;
    },
  },

  mutations: {
    setCart(state, cart) {
      state.cart = cart;
    },

    setCartItems(state, cartItems) {
      state.cartItems = cartItems;
    },

    setCartItemsConcept(state, cartItems) {
      state.cartItemsConcept = cartItems;
    },

    setModalIsOpen(state, isOpen = false) {
      state.modalIsOpen = isOpen;
    },

    setAlertIsOpen(state, isOpen = false) {
      state.alertIsOpen = isOpen;
    },

    setDateForCartItem(state, [id, key, date]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem[key] = parseInt(date, 10);
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setAmountOfNightsForCartItem(state, [id, nights]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.amountOfNights = parseInt(nights, 10);
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setAmountOfRoomsForCartItem(state, [id, amountOfRooms]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.amountOfRooms = parseInt(amountOfRooms, 10);
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setCapacityOfRoomsForCartItem(state, [id, capacity]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.capacityOfRooms = parseInt(capacity, 10);
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setOptionsForCartItem(state, [id, options]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.options = options;
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setExperiencesForCartItem(state, [id, experiences]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.experiences = experiences;
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setSelectedRoomForCartItem(state, [id, room]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.selectedRoom = room;
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setRoomOptionsForCartItem(state, [id, roomOptions]) {
      let cartItems = cloneDeep(state.cartItems);

      cartItems = cartItems.map((cartItem) => {
        if (cartItem.id === id) {
          cartItem.roomOptions = roomOptions;
        }

        return cartItem;
      });

      state.cartItems = cartItems;
      state.cartItemsConcept = cartItems;
    },

    setCartField(state, [key, value]) {
      state.cart[key] = value;
    },

    setOpenDatePicker(state, id) {
      state.openDatePicker = id;
    },

    setIsLoading(state, isLoading = false) {
      state.isLoading = isLoading;
    },

    setErrors(state, error) {
      state.errors = state.errors.push(error);
    },
  },
});

export default store;
